import { useState, useRef, useMemo } from "react";
import { DownloadSimple, FileImage, FileXls, CircleNotch, CaretRight, CaretDown, Lightning, WarningDiamond } from "@phosphor-icons/react";
import { toPng } from "html-to-image";
import jsPDF from "jspdf";
import * as XLSX from "xlsx";
import { addDays, differenceInCalendarDays, format, eachWeekOfInterval, eachMonthOfInterval, eachQuarterOfInterval, eachDayOfInterval } from "date-fns";
import { DEPARTMENT_COLORS, STATUS_COLORS } from "@/constants";

const ZOOM_LEVELS = { day: 42, week: 15, month: 5, quarter: 2 };
const ROW_HEIGHT = 34;

const hexToRgba = (hex, alpha) => {
  const h = hex.replace("#", "");
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const GanttChart = ({ rows, milestones = [], onReschedule, loading, canDrag = false, onHeaderClick, onTaskClick, onPhaseClick, exportRef, exportFileName }) => {
  const [zoom, setZoom] = useState("week");
  const [drag, setDrag] = useState(null);
  const [showCriticalPath, setShowCriticalPath] = useState(true);
  const containerRef = useRef(null);
  const justDraggedRef = useRef(false);
  const pxPerDay = ZOOM_LEVELS[zoom];

  const [timelineStart, timelineEnd] = useMemo(() => {
    const dates = [];
    rows.forEach((r) => { if (r.start) dates.push(new Date(r.start)); if (r.end) dates.push(new Date(r.end)); });
    milestones.forEach((m) => { if (m.target_date) dates.push(new Date(m.target_date)); });
    if (dates.length === 0) { const now = new Date(); return [addDays(now, -7), addDays(now, 30)]; }
    const min = new Date(Math.min(...dates));
    const max = new Date(Math.max(...dates));
    return [addDays(min, -5), addDays(max, 10)];
  }, [rows, milestones]);

  const totalDays = Math.max(differenceInCalendarDays(timelineEnd, timelineStart), 30);
  const totalWidth = totalDays * pxPerDay;
  const xFor = (date) => differenceInCalendarDays(new Date(date), timelineStart) * pxPerDay;

  const ticks = useMemo(() => {
    try {
      if (zoom === "day") return eachDayOfInterval({ start: timelineStart, end: timelineEnd }).map((d) => ({ date: d, label: format(d, "d MMM") }));
      if (zoom === "week") return eachWeekOfInterval({ start: timelineStart, end: timelineEnd }, { weekStartsOn: 1 }).map((d) => ({ date: d, label: format(d, "d MMM") }));
      if (zoom === "month") return eachMonthOfInterval({ start: timelineStart, end: timelineEnd }).map((d) => ({ date: d, label: format(d, "MMM yyyy") }));
      return eachQuarterOfInterval({ start: timelineStart, end: timelineEnd }).map((d) => ({ date: d, label: `Q${format(d, "Q")} ${format(d, "yyyy")}` }));
    } catch { return []; }
  }, [zoom, timelineStart, timelineEnd]);

  const todayX = xFor(new Date());
  const rowY = (index) => index * ROW_HEIGHT + ROW_HEIGHT / 2;
  const idToIndex = useMemo(() => Object.fromEntries(rows.map((r, i) => [r.id, i])), [rows]);

  // Tasks completed after their due date: for these we render an extra "overrun" segment
  // on the bar from due_date to actual_completion_date, and surface the exact delay in days.
  const delayInfo = useMemo(() => {
    const map = {};
    rows.forEach((r) => {
      if (r.type !== "task" || r.status !== "Completed" || !r.end || !r.actual_completion_date) return;
      const due = new Date(r.end);
      const done = new Date(r.actual_completion_date);
      const days = differenceInCalendarDays(done, due);
      if (days > 0) map[r.id] = { days, completedOn: done };
    });
    return map;
  }, [rows]);
  const rowDelay = (r) => {
    if (r.isProjectHeader || !r.end || (r.type !== "task" && r.type !== "phase")) return null;
    if (r.status === "Blocked" || r.onHold) return null;
    if (r.status === "Completed") {
      if (r.type === "task" && r.actual_completion_date) {
        const d = differenceInCalendarDays(new Date(r.actual_completion_date), new Date(r.end));
        if (d > 0) return { days: d, kind: "late" };
      }
      return null;
    }
    const d = differenceInCalendarDays(new Date(), new Date(r.end));
    if (d > 0) return { days: d, kind: "overdue" };
    return null;
  };

  // Critical Path Method (CPM): computes zero-slack tasks across the dependency graph
  // (duration-based forward/backward pass, independent of any disconnected chains).
  const criticalTaskIds = useMemo(() => {
    const taskRows = rows.filter((r) => r.type === "task" && r.start && r.end);
    if (taskRows.length === 0) return new Set();
    const rowById = Object.fromEntries(taskRows.map((r) => [r.id, r]));
    const durationOf = (r) => Math.max(differenceInCalendarDays(new Date(r.end), new Date(r.start)), 0);
    const successors = {};
    taskRows.forEach((r) => { successors[r.id] = []; });
    taskRows.forEach((r) => {
      (r.dependencies || []).forEach((depId) => { if (successors[depId]) successors[depId].push(r.id); });
    });

    const es = {};
    const computeES = (id, visiting) => {
      if (es[id] !== undefined) return es[id];
      if (visiting.has(id)) return 0;
      visiting.add(id);
      const r = rowById[id];
      const preds = (r.dependencies || []).filter((d) => rowById[d]);
      let val = 0;
      preds.forEach((p) => { val = Math.max(val, computeES(p, visiting) + durationOf(rowById[p])); });
      es[id] = val;
      return val;
    };
    taskRows.forEach((r) => computeES(r.id, new Set()));

    let projectDuration = 0;
    taskRows.forEach((r) => { projectDuration = Math.max(projectDuration, es[r.id] + durationOf(r)); });

    const lf = {};
    const computeLF = (id, visiting) => {
      if (lf[id] !== undefined) return lf[id];
      if (visiting.has(id)) return projectDuration;
      visiting.add(id);
      const succ = successors[id] || [];
      const val = succ.length === 0 ? projectDuration : Math.min(...succ.map((s) => computeLF(s, visiting) - durationOf(rowById[s])));
      lf[id] = val;
      return val;
    };
    taskRows.forEach((r) => computeLF(r.id, new Set()));

    const critical = new Set();
    taskRows.forEach((r) => {
      const ls = lf[r.id] - durationOf(r);
      if (ls - es[r.id] <= 0) critical.add(r.id);
    });
    return critical;
  }, [rows]);

  const handleMouseDown = (e, row) => {
    if (!canDrag || row.type !== "task" || !row.draggable) return;
    e.preventDefault();
    setDrag({ id: row.id, startX: e.clientX, origStart: new Date(row.start), origEnd: new Date(row.end), deltaPx: 0 });
    const onMove = (ev) => setDrag((d) => (d ? { ...d, deltaPx: ev.clientX - d.startX } : d));
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      setDrag((d) => {
        if (d) {
          const deltaDays = Math.round(d.deltaPx / pxPerDay);
          if (deltaDays !== 0) {
            onReschedule(d.id, addDays(d.origStart, deltaDays), addDays(d.origEnd, deltaDays));
            justDraggedRef.current = true;
          }
        }
        return null;
      });
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const handleBarClick = (row) => {
    if (justDraggedRef.current) { justDraggedRef.current = false; return; }
    if (row.type === "task") onTaskClick?.(row.id);
    else if (row.type === "phase" && !row.isProjectHeader) onPhaseClick?.(row.id);
  };

  const exportPng = async () => {
    const target = exportRef?.current || containerRef.current;
    if (!target) return;
    const dataUrl = await toPng(target, { backgroundColor: "#ffffff" });
    const link = document.createElement("a");
    link.download = `${exportFileName ? exportFileName.replace(/[^a-z0-9]+/gi, "-").toLowerCase() : "gantt-chart"}.png`;
    link.href = dataUrl;
    link.click();
  };

  const exportExcel = () => {
    const fmt = (d) => (d ? format(new Date(d), "d MMM yyyy") : "");
    const data = rows.filter((r) => !r.isProjectHeader).map((r) => {
      const d = rowDelay(r);
      return {
        Type: r.type === "phase" ? "Phase" : "Task",
        Name: r.label,
        "Responsible Person": r.type === "task" ? (r.assignee_names || "Unassigned") : "",
        Department: r.department || "",
        "Start Date": fmt(r.start),
        "End / Due Date": fmt(r.end),
        Status: r.status || "",
        "Completion %": r.completion_pct ?? "",
        "Completed On": r.type === "task" ? fmt(r.actual_completion_date) : "",
        Delay: d ? `+${d.days}d ${d.kind === "late" ? "late" : "overdue"}` : "",
      };
    });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data), "Gantt");
    if (milestones.length > 0) {
      const ms = milestones.map((m) => ({ Milestone: m.name, "Target Date": fmt(m.target_date), Status: m.status }));
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(ms), "Milestones");
    }
    XLSX.writeFile(wb, `${exportFileName ? exportFileName.replace(/[^a-z0-9]+/gi, "-").toLowerCase() : "gantt-chart"}.xlsx`);
  };

  const exportPdf = async () => {
    const target = exportRef?.current || containerRef.current;
    if (!target) return;
    const dataUrl = await toPng(target, { backgroundColor: "#ffffff" });
    const img = new Image();
    img.src = dataUrl;
    await new Promise((r) => { img.onload = r; });
    const pdf = new jsPDF({ orientation: "landscape", unit: "px", format: [img.width, img.height] });
    pdf.addImage(dataUrl, "PNG", 0, 0, img.width, img.height);
    pdf.save(`${exportFileName ? exportFileName.replace(/[^a-z0-9]+/gi, "-").toLowerCase() : "gantt-chart"}.pdf`);
  };

  return (
    <>
    <div className="card-flat hidden md:block" data-testid="gantt-chart">
      <div className="px-4 py-2.5 border-b border-border">
        <div className="flex items-center flex-wrap gap-x-4 gap-y-2">
          <span className="label-caps">Gantt Timeline</span>
          <div className="flex border border-border overflow-hidden" style={{ borderRadius: "2px" }}>
            {Object.keys(ZOOM_LEVELS).map((z) => (
              <button key={z} data-testid={`gantt-zoom-${z}-btn`} onClick={() => setZoom(z)}
                className={`px-2.5 py-1 text-[11px] font-semibold uppercase transition-colors ${zoom === z ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`}>{z}</button>
            ))}
          </div>
          <button data-testid="gantt-critical-path-toggle" onClick={() => setShowCriticalPath((v) => !v)}
            className={`px-2 py-1 text-[11px] font-semibold uppercase border flex items-center gap-1 transition-colors ${showCriticalPath ? "bg-destructive/10 text-destructive border-destructive/40" : "border-border text-muted-foreground hover:bg-muted"}`}
            style={{ borderRadius: "2px" }}>
            <Lightning size={12} weight={showCriticalPath ? "fill" : "regular"} />Critical Path
          </button>
          <div className="ml-auto flex items-center gap-1.5">
            <button data-testid="gantt-export-png-btn" onClick={exportPng} className="btn-outline-navy flex items-center gap-1.5 !py-1 !px-2 !text-[11px]"><FileImage size={12} />PNG</button>
            <button data-testid="gantt-export-pdf-btn" onClick={exportPdf} className="btn-outline-navy flex items-center gap-1.5 !py-1 !px-2 !text-[11px]"><DownloadSimple size={12} />PDF</button>
            <button data-testid="gantt-export-excel-btn" onClick={exportExcel} className="btn-outline-navy flex items-center gap-1.5 !py-1 !px-2 !text-[11px]"><FileXls size={12} />Excel</button>
          </div>
        </div>
        <div className="flex items-center gap-x-2.5 gap-y-1 text-[10px] text-muted-foreground flex-wrap mt-2">
          {Object.entries(DEPARTMENT_COLORS).map(([dept, color]) => (
            <span key={dept} className="flex items-center gap-1"><span className="w-2 h-2 shrink-0" style={{ background: color, borderRadius: "1px" }} />{dept}</span>
          ))}
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center gap-2 py-16 text-muted-foreground" data-testid="gantt-loading">
          <CircleNotch size={20} className="animate-spin" /> Rendering timeline...
        </div>
      )}

      {!loading && rows.length === 0 && (
        <div className="p-10 text-center text-sm font-mono text-muted-foreground" data-testid="gantt-empty">&gt; no phases or tasks to display</div>
      )}

      {!loading && rows.length > 0 && (
        <div className="flex overflow-hidden" ref={containerRef}>
          <div className="w-56 shrink-0 border-r border-border bg-card">
            <div style={{ height: 40 }} className="border-b border-border" />
            {rows.map((r) => (
              <div key={r.id} style={{ height: ROW_HEIGHT }}
                className={`flex items-center gap-1.5 px-3 border-b text-xs ${r.isProjectHeader ? "bg-primary/5 border-border font-display font-bold text-primary cursor-pointer hover:bg-primary/10 transition-colors" : r.type === "task" || (r.type === "phase" && !r.isProjectHeader) ? "border-border/50 cursor-pointer hover:bg-muted/60 transition-colors" : "border-border/50"}`}
                data-testid={`gantt-row-label-${r.id}`}
                onClick={r.isProjectHeader ? () => onHeaderClick?.(r.projectId) : r.type === "task" ? () => onTaskClick?.(r.id) : r.type === "phase" ? () => onPhaseClick?.(r.id) : undefined}
              >
                {r.isProjectHeader && (r.expanded ? <CaretDown size={12} weight="bold" /> : <CaretRight size={12} weight="bold" />)}
                <span className={`truncate min-w-0 flex-1 ${r.isProjectHeader ? "" : r.type === "phase" ? "font-semibold" : "pl-4 text-muted-foreground"}`}>{r.label}</span>
                {r.type === "task" && <span className="text-[10px] font-mono text-muted-foreground shrink-0" data-testid={`gantt-row-pct-${r.id}`}>{Math.min(100, Math.max(0, r.completion_pct || 0))}%</span>}
                {r.type === "phase" && !r.isProjectHeader && r.completion_pct !== undefined && <span className="text-[10px] font-mono font-bold text-primary shrink-0" data-testid={`gantt-row-pct-${r.id}`}>{Math.min(100, Math.max(0, r.completion_pct || 0))}%</span>}
              </div>
            ))}
            {milestones.length > 0 && (
              <div style={{ height: ROW_HEIGHT }} className="flex items-center px-3 border-b border-border/50 text-xs font-semibold text-accent">Milestones</div>
            )}
          </div>
          <div className="flex-1 overflow-x-auto relative">
            <div style={{ width: totalWidth, position: "relative" }}>
              <div style={{ height: 40 }} className="border-b border-border relative">
                {ticks.map((t, i) => (
                  <div key={i} style={{ position: "absolute", left: xFor(t.date) }} className="text-[10px] font-mono text-muted-foreground px-1 border-l border-border/50 h-full flex items-center">
                    {t.label}
                  </div>
                ))}
              </div>
              <div style={{ position: "relative", height: rows.length * ROW_HEIGHT + (milestones.length > 0 ? ROW_HEIGHT : 0) }}>
                {todayX > 0 && todayX < totalWidth && (
                  <div style={{ position: "absolute", left: todayX, top: 0, bottom: 0, width: 1, background: "#dc2626", zIndex: 5 }} />
                )}
                {rows.map((r, i) => {
                  if (r.isProjectHeader) {
                    return (
                      <div key={r.id} data-testid={`gantt-header-band-${r.id}`} onClick={() => onHeaderClick?.(r.projectId)}
                        style={{ position: "absolute", top: i * ROW_HEIGHT, left: 0, width: totalWidth, height: ROW_HEIGHT }}
                        className="bg-primary/5 border-b border-border cursor-pointer hover:bg-primary/10 transition-colors" />
                    );
                  }
                  if (!r.start || !r.end) return null;
                  const left = xFor(r.start);
                  const width = Math.max(xFor(r.end) - left, 8);
                  const isDragging = drag?.id === r.id;
                  const color = r.type === "phase" ? (DEPARTMENT_COLORS[r.department] || "#64748b") : (STATUS_COLORS[r.status] || "#64748b");
                  const pct = Math.min(100, Math.max(0, r.completion_pct || 0));
                  const isCritical = showCriticalPath && r.type === "task" && criticalTaskIds.has(r.id);
                  const delay = r.type === "task" ? delayInfo[r.id] : null;
                  const tooltipDelay = delay ? ` · Completed ${format(delay.completedOn, "d MMM")} — ${delay.days}d late` : "";
                  return (
                    <div key={r.id} style={{ position: "absolute", top: i * ROW_HEIGHT, left: 0, right: 0, height: ROW_HEIGHT }} className="border-b border-border/30">
                      <div
                        data-testid={`gantt-bar-${r.id}`}
                        onMouseDown={(e) => handleMouseDown(e, r)}
                        onClick={() => handleBarClick(r)}
                        title={r.type === "task" ? `${r.label} (${r.status} · ${pct}% complete)${isCritical ? " · On Critical Path" : ""}${tooltipDelay}` : `${r.label} (${r.status})`}
                        style={{
                          position: "absolute", left: left + (isDragging ? drag.deltaPx : 0), width, top: 6, height: ROW_HEIGHT - 12,
                          background: color, borderRadius: 2,
                          cursor: r.type === "task" ? (r.draggable && canDrag ? "grab" : "pointer") : "pointer",
                          opacity: r.type === "phase" ? 0.55 : 1,
                          border: isCritical ? "1.5px solid #dc2626" : (r.type === "phase" ? `1px solid ${color}` : `1px solid ${hexToRgba(color, 0.9)}`),
                          boxShadow: isCritical ? "0 0 0 1px rgba(220,38,38,0.35)" : undefined,
                          overflow: "hidden",
                        }}
                        className="flex items-center px-2 text-[10px] text-white font-semibold truncate shadow-sm"
                      >
                        {r.type === "task" && pct < 100 && (
                          <div data-testid={`gantt-bar-fill-${r.id}`} style={{ position: "absolute", left: `${pct}%`, top: 0, bottom: 0, right: 0, background: "rgba(255,255,255,0.22)", zIndex: 0 }} />
                        )}
                        {isCritical && (
                          <Lightning size={9} weight="fill" color="#dc2626" style={{ position: "relative", zIndex: 1, marginRight: 2, flexShrink: 0 }} data-testid={`gantt-critical-icon-${r.id}`} />
                        )}
                        <span style={{ position: "relative", zIndex: 1, textShadow: "0 1px 2px rgba(0,0,0,0.6)" }}>{r.type === "task" ? `${r.label} · ${pct}%` : ""}</span>
                      </div>
                    </div>
                  );
                })}
                <svg style={{ position: "absolute", top: 0, left: 0, width: totalWidth, height: rows.length * ROW_HEIGHT, pointerEvents: "none" }}>
                  <defs>
                    <marker id="gantt-arrow" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
                      <path d="M0,0 L6,3 L0,6 Z" fill="#94a3b8" />
                    </marker>
                    <marker id="gantt-arrow-critical" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
                      <path d="M0,0 L6,3 L0,6 Z" fill="#dc2626" />
                    </marker>
                  </defs>
                  {rows.map((r) => {
                    if (r.type !== "task" || !r.dependencies?.length) return null;
                    return r.dependencies.map((depId) => {
                      const predIndex = idToIndex[depId];
                      const curIndex = idToIndex[r.id];
                      if (predIndex === undefined || curIndex === undefined) return null;
                      const predRow = rows[predIndex];
                      if (!predRow.end) return null;
                      const predX = xFor(predRow.end);
                      const predY = rowY(predIndex);
                      const curX = xFor(r.start);
                      const curY = rowY(curIndex);
                      const isCriticalEdge = showCriticalPath && criticalTaskIds.has(r.id) && criticalTaskIds.has(depId);
                      return (
                        <path key={`${depId}-${r.id}`} d={`M${predX},${predY} L${predX + 10},${predY} L${predX + 10},${curY} L${curX},${curY}`}
                          fill="none" stroke={isCriticalEdge ? "#dc2626" : "#94a3b8"} strokeWidth={isCriticalEdge ? 2 : 1.5}
                          markerEnd={isCriticalEdge ? "url(#gantt-arrow-critical)" : "url(#gantt-arrow)"} />
                      );
                    });
                  })}
                </svg>
                {milestones.map((m) => m.target_date && (
                  <div key={m.id} data-testid={`gantt-milestone-${m.id}`}
                    style={{ position: "absolute", left: xFor(m.target_date) - 6, top: rows.length * ROW_HEIGHT + ROW_HEIGHT / 2 - 6, width: 12, height: 12, transform: "rotate(45deg)", background: m.status === "Achieved" ? "#16a34a" : m.status === "Missed" ? "#dc2626" : "#38bdf8" }}
                    title={m.name}
                  />
                ))}
              </div>
            </div>
          </div>
          <div className="w-20 shrink-0 border-l border-border bg-card" data-testid="gantt-delay-column">
            <div style={{ height: 40 }} className="border-b border-border flex items-center justify-center">
              <span className="label-caps !text-[9px]">Delay</span>
            </div>
            {rows.map((r) => {
              const d = rowDelay(r);
              return (
                <div key={r.id} style={{ height: ROW_HEIGHT }} data-testid={`gantt-delay-col-${r.id}`}
                  title={d ? (d.kind === "late" ? `Completed ${d.days} day${d.days > 1 ? "s" : ""} late` : `${d.days} day${d.days > 1 ? "s" : ""} overdue`) : undefined}
                  className={`flex items-center justify-center border-b text-[10px] font-mono font-bold ${r.isProjectHeader ? "bg-primary/5 border-border" : "border-border/50"}`}>
                  {d ? (
                    <span className={d.kind === "late" ? "text-amber-600" : "text-destructive"}>+{d.days}d</span>
                  ) : (
                    !r.isProjectHeader && <span className="text-muted-foreground/30">—</span>
                  )}
                </div>
              );
            })}
            {milestones.length > 0 && <div style={{ height: ROW_HEIGHT }} className="border-b border-border/50" />}
          </div>
        </div>
      )}
    </div>

    <div className="card-flat md:hidden" data-testid="gantt-mobile-list">
      <div className="px-4 py-3 border-b border-border">
        <span className="label-caps">Task List</span>
        <p className="text-xs text-muted-foreground mt-1">Full Gantt timeline view is available on desktop</p>
      </div>
      {loading && (
        <div className="flex items-center justify-center gap-2 py-10 text-muted-foreground" data-testid="gantt-mobile-loading">
          <CircleNotch size={18} className="animate-spin" /> Loading tasks...
        </div>
      )}
      {!loading && rows.length === 0 && (
        <div className="p-6 text-center text-sm font-mono text-muted-foreground" data-testid="gantt-mobile-empty">&gt; no phases or tasks to display</div>
      )}
      {!loading && rows.length > 0 && (
        <div className="divide-y divide-border">
          {rows.map((r) => {
            if (r.isProjectHeader) {
              return (
                <div key={r.id} data-testid={`gantt-mobile-row-${r.id}`} onClick={() => onHeaderClick?.(r.projectId)}
                  className="flex items-center gap-2 px-4 py-2.5 bg-primary/5 font-display font-bold text-sm text-primary cursor-pointer">
                  {r.expanded ? <CaretDown size={12} weight="bold" /> : <CaretRight size={12} weight="bold" />}
                  {r.label}
                </div>
              );
            }
            if (r.type === "phase") {
              return (
                <div key={r.id} data-testid={`gantt-mobile-row-${r.id}`} onClick={() => onPhaseClick?.(r.id)} className="px-4 py-2 flex items-center justify-between text-xs font-semibold text-muted-foreground bg-muted/40 cursor-pointer active:bg-muted/60 transition-colors">
                  <span>{r.label}</span>
                  {r.completion_pct !== undefined && <span className="font-mono text-primary">{Math.min(100, Math.max(0, r.completion_pct || 0))}%</span>}
                </div>
              );
            }
            const pct = Math.min(100, Math.max(0, r.completion_pct || 0));
            const isCritical = showCriticalPath && criticalTaskIds.has(r.id);
            const delay = delayInfo[r.id];
            return (
              <div key={r.id} data-testid={`gantt-mobile-row-${r.id}`} onClick={() => onTaskClick?.(r.id)}
                className="flex items-center justify-between gap-3 px-4 py-3 pl-6 cursor-pointer active:bg-muted/60 transition-colors">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate flex items-center gap-1">
                    {isCritical && <Lightning size={11} weight="fill" color="#dc2626" data-testid={`gantt-mobile-critical-icon-${r.id}`} />}
                    {r.label}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">{r.status} · {pct}% · {r.end ? format(new Date(r.end), "d MMM") : "No due date"}</div>
                  {delay && (
                    <div className="text-xs font-semibold text-amber-600 mt-0.5" data-testid={`gantt-mobile-delay-${r.id}`}>
                      <WarningDiamond size={11} weight="fill" className="inline mr-1" />
                      Completed {format(delay.completedOn, "d MMM")} — {delay.days}d late
                    </div>
                  )}
                </div>
                <div className="text-xs font-mono font-semibold shrink-0" style={{ color: STATUS_COLORS[r.status] || "#64748b" }} data-testid={`gantt-mobile-pct-${r.id}`}>{pct}%</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
    </>
  );
};
